import { Injectable, inject } from '@angular/core';
import { fhirR4 } from '@smile-cdr/fhirts';
import { TranslateService } from '@ngx-translate/core';
import get from 'lodash/get';
import { AppContext } from '../models/AppContext';
import { CrdResponse } from '../models/CrdResponse';
import { CrdError, CrdErrorType } from '../models/CrdError';
import { MessageBoxState } from '../models/MessageBoxState';
import { PA_NEEDED_CODES } from '../models/dtr-types';
import { DataService } from '../services/data.service';
import { EnvService } from '../services/env.service';
import { ExtensionService } from '../services/extension.service';
import { LoggerService } from '../services/logger.service';
import { ContextService } from './context.service';

export type MarshalDecision =
  | { kind: 'relaunch'; qr: fhirR4.QuestionnaireResponse; task?: fhirR4.Task }
  | { kind: 'cdex'; task: fhirR4.Task }
  | { kind: 'new' };

export type NewLaunchDecision =
  | {
      kind: 'launch';
      canonicalUrl: string | undefined;
      coverage: unknown;
      order: fhirR4.Resource;
    }
  | { kind: 'messageBox'; state: MessageBoxState; params?: Record<string, unknown> };

/**
 * Owns launch context resolution and the pre-population decision flow:
 * SMART context marshaling, PA-endpoint discovery and the CRD prior-auth
 * decision. Returns plain decisions; the component performs navigation.
 */
@Injectable({
  providedIn: 'root',
})
export class LaunchContextService {
  private readonly contextService = inject(ContextService);
  private readonly dataService = inject(DataService);
  private readonly env = inject(EnvService);
  private readonly logger = inject(LoggerService);
  private readonly translate = inject(TranslateService);

  checkConfigError(): boolean {
    if (this.env.configError) {
      this.logError(this.env.configError);
      return true;
    }
    return false;
  }

  logError(err: unknown): void {
    this.logger.error(err);
  }

  handleLaunchError(err: unknown): boolean {
    this.logError(err);
    return this.dataService.sofLaunchDone;
  }

  async initAndCheckContext(advance: () => void): Promise<AppContext> {
    advance();
    const appContext = await this.contextService.getAppContext();
    await this.dataService.loadFhirUser();
    appContext.marshalContext =
      await this.contextService.marshalContext(appContext);
    if (appContext.fhirContext && !appContext.marshalContext?.success) {
      if (appContext.fhirContext.length === 0) {
        throw new Error(this.translate.instant('Error.NoFhirContext'));
      }
      throw new Error(this.translate.instant('Error.Marshaling'));
    }
    await this.initEndpoints(appContext);
    advance();
    return appContext;
  }

  async initEndpoints(appContext: AppContext) {
    const payorOrg = await this.contextService.getPayorOrg(appContext);
    await this.contextService.initPAEndpoints(payorOrg);
  }

  classifyMarshalContext(appContext: AppContext): MarshalDecision {
    const task = get(appContext, 'marshalContext.marshalingRequest.Task');
    const qr = get(
      appContext,
      'marshalContext.marshalingRequest.QuestionnaireResponse',
    );
    if (task && qr) return { kind: 'relaunch', qr, task };
    if (task) return { kind: 'cdex', task };
    if (qr) return { kind: 'relaunch', qr };
    return { kind: 'new' };
  }

  async resolveNewLaunch(appContext: AppContext): Promise<NewLaunchDecision> {
    const order = await this.contextService.getOrderResource(appContext);
    const coverage = get(
      appContext,
      'marshalContext.marshalingRequest.Coverage',
    );

    if (this.env.environment.standalone === 'dtr') {
      const canonicalUrl = ExtensionService.getCanonicalUrl(
        order,
        appContext.canonicalUrl,
      );
      return { kind: 'launch', canonicalUrl, coverage, order };
    }

    let paParameters;
    try {
      paParameters = await this.getPAParameters(appContext);
    } catch (err) {
      if (err instanceof CrdError && err.type === CrdErrorType.HookNotFound) {
        this.logError(err.cause ?? err);
        return {
          kind: 'messageBox',
          state: MessageBoxState.UnableToProcessCRDResponseHookNotFound,
          params: { hookName: err.data?.['hookName'] },
        };
      }
      this.logError(err);
      return {
        kind: 'messageBox',
        state: MessageBoxState.UnableToProcessCRDResponse,
      };
    }
    const canonicalUrl = get(paParameters, 'canonicalUrl');
    const paNeeded = get(paParameters, 'paNeeded.valueCode');
    if (paNeeded === 'auth-needed') {
      return { kind: 'launch', canonicalUrl, coverage, order };
    } else if (paNeeded === 'conditional') {
      return { kind: 'messageBox', state: MessageBoxState.PAConditional };
    }
    return { kind: 'messageBox', state: MessageBoxState.PANotRequired };
  }

  async getPAParameters(appContext: AppContext) {
    const crdResponse = await this.contextService.callCrdIfRequired(appContext);
    const canonicalUrl = await this.resolveCanonicalFromCrd(
      crdResponse,
      appContext,
    );
    const paNeeded = get(crdResponse, 'context.paNeeded');
    if (!paNeeded) {
      throw new Error(
        'The paNeeded property is not present in the crdResponse.',
      );
    }
    if (!PA_NEEDED_CODES.includes(paNeeded.valueCode)) {
      throw new Error('The paNeeded property is not valid.');
    }
    return { canonicalUrl, paNeeded };
  }

  async resolveCanonicalFromCrd(
    crdResponse: CrdResponse,
    appContext: AppContext,
  ) {
    let order = crdResponse.context.order;
    if (!order) {
      const orderId = crdResponse.context.orderId ?? '';
      order = await this.dataService.getResource(orderId);
    }
    return ExtensionService.getCanonicalUrl(
      order as fhirR4.Resource,
      appContext.canonicalUrl,
    );
  }
}
